import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { type CostBreakdownFormValues, costBreakdownFormSchema } from "../../schemas"
import { getCostBreakdownDefaultFormValues } from "../../utils/formDefaults"

import { CostBreakdownForm } from "./CostBreakdownForm"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"

interface CostBreakdownDialogProps {
  goodsReceipt?: GoodsReceipt
  grCostBreakdown?: CostBreakdownFormValues
  onClose: (isDirty: boolean, currentData?: CostBreakdownFormValues) => void
  onSubmit: (data: CostBreakdownFormValues, isDirty: boolean) => void
}

export const CostBreakdownDialog: FC<CostBreakdownDialogProps> = ({
  goodsReceipt,
  grCostBreakdown,
  onClose,
  onSubmit,
}) => {
  const isEditMode = !!grCostBreakdown

  const defaultValues = getCostBreakdownDefaultFormValues(goodsReceipt, grCostBreakdown)

  const methods = useForm<CostBreakdownFormValues>({
    resolver: zodResolver(costBreakdownFormSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues,
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
    getValues,
  } = methods

  const onFormSubmit = (data: CostBreakdownFormValues) => {
    onSubmit(data, isDirty)
  }

  const handleClose = () => {
    onClose(isDirty, isDirty ? getValues() : undefined)
    if (!isDirty) {
      reset()
    }
  }

  return (
    <Dialog open onClose={handleClose} maxWidth='sm' fullWidth data-testid='cost-breakdown-dialog'>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader
            title={isEditMode ? "Edit Cost Breakdown: Goods Receipt" : "Add Cost Breakdown: Goods Receipt"}
            onClose={handleClose}
          />
          <DialogContent classes={{ root: "p-10" }}>
            <CostBreakdownForm goodsReceipt={goodsReceipt} />
          </DialogContent>
          <DialogActions className='flex justify-between px-10 pb-6'>
            <Button appearance='outlined' onClick={handleClose} data-testid='cost-breakdown-cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              color='primary'
              type='submit'
              disabled={!isValid}
              data-testid='cost-breakdown-continue-button'
            >
              Continue
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
